package fr.manyverse.app.presentation.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.Universe
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.back
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.configure_table
import manyverse.shared.generated.resources.create_universe
import manyverse.shared.generated.resources.custom_universe_snapshot_warning
import manyverse.shared.generated.resources.launch_table
import manyverse.shared.generated.resources.select_universe
import manyverse.shared.generated.resources.table_name
import manyverse.shared.generated.resources.table_name_placeholder
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TableConfigurationScreen(
    gameName: String,
    onGameNameChange: (String) -> Unit,
    universes: List<Universe>,
    selectedUniverseId: String?,
    onUniverseSelected: (String) -> Unit,
    onCreateUniverse: () -> Unit,
    onCancel: () -> Unit,
    onLaunch: () -> Unit,
    isLaunchEnabled: Boolean = false,
    errorMessage: String? = null,
    modifier: Modifier = Modifier
) {
    var universeDropdownExpanded by remember { mutableStateOf(false) }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.configure_table)) },
                navigationIcon = {
                    IconButton(onClick = onCancel) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = stringResource(Res.string.back)
                        )
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = modifier
                .fillMaxSize()
                .padding(padding)
                .imePadding()
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            // Universe selector (required)
            ExposedDropdownMenuBox(
                expanded = universeDropdownExpanded,
                onExpandedChange = { universeDropdownExpanded = it },
                modifier = Modifier.fillMaxWidth()
            ) {
                val selectedLabel = universes.firstOrNull { it.id == selectedUniverseId }
                    ?.let { universeDisplayLabel(it) } ?: ""
                OutlinedTextField(
                    value = selectedLabel,
                    onValueChange = {},
                    readOnly = true,
                    label = { Text(stringResource(Res.string.select_universe)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = universeDropdownExpanded) },
                    modifier = Modifier
                        .fillMaxWidth()
                        .menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable)
                )
                ExposedDropdownMenu(
                    expanded = universeDropdownExpanded,
                    onDismissRequest = { universeDropdownExpanded = false }
                ) {
                    universes.forEach { universe ->
                        DropdownMenuItem(
                            text = { Text(universeDisplayLabel(universe)) },
                            onClick = {
                                onUniverseSelected(universe.id)
                                universeDropdownExpanded = false
                            }
                        )
                    }
                    HorizontalDivider()
                    DropdownMenuItem(
                        text = { Text(stringResource(Res.string.create_universe)) },
                        leadingIcon = { Icon(Icons.Default.Add, contentDescription = null) },
                        onClick = {
                            universeDropdownExpanded = false
                            onCreateUniverse()
                        }
                    )
                }
            }

            val selectedUniverse = universes.firstOrNull { it.id == selectedUniverseId }
            if (selectedUniverse != null) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.Top
                ) {
                    Icon(
                        imageVector = Icons.Default.Warning,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.padding(top = 1.dp)
                    )
                    Text(
                        text = stringResource(Res.string.custom_universe_snapshot_warning),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }

            // Game name input (required)
            OutlinedTextField(
                value = gameName,
                onValueChange = onGameNameChange,
                label = { Text(stringResource(Res.string.table_name)) },
                placeholder = { Text(stringResource(Res.string.table_name_placeholder)) },
                keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
                modifier = Modifier.fillMaxWidth(),
                singleLine = true
            )

            if (errorMessage != null) {
                Text(
                    text = errorMessage,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.error
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OutlinedButton(
                    onClick = onCancel,
                    modifier = Modifier.weight(1f)
                ) {
                    Text(stringResource(Res.string.cancel))
                }

                Button(
                    onClick = onLaunch,
                    enabled = isLaunchEnabled,
                    modifier = Modifier.weight(1f)
                ) {
                    Icon(Icons.Default.PlayArrow, contentDescription = null)
                    Spacer(Modifier.width(8.dp))
                    Text(stringResource(Res.string.launch_table))
                }
            }
        }
    }
}

private fun universeDisplayLabel(universe: Universe): String = universe.displayName
